import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  InputNumber,
  Select,
  Skeleton,
  Table,
} from 'antd';
import {
  AiOutlineBarChart,
  AiOutlineReload,
  AiOutlineShoppingCart,
} from 'react-icons/ai';
import { LuBadgeDollarSign, LuCalendarRange } from 'react-icons/lu';
import { MdAttachMoney, MdOutlineInventory2 } from 'react-icons/md';
import Card from '../../../components/Global/Card';
import { CustomService } from '../../../services/api/custom/CustomService';

const statusOptions = [
  { label: 'Aprovados', value: 'aprovado_em' },
  { label: 'Entregues', value: 'entregue_em' },
  { label: 'Pagos', value: 'pago_em' },
];

const monthOptions = [
  { label: 'Janeiro', value: 1 },
  { label: 'Fevereiro', value: 2 },
  { label: 'Março', value: 3 },
  { label: 'Abril', value: 4 },
  { label: 'Maio', value: 5 },
  { label: 'Junho', value: 6 },
  { label: 'Julho', value: 7 },
  { label: 'Agosto', value: 8 },
  { label: 'Setembro', value: 9 },
  { label: 'Outubro', value: 10 },
  { label: 'Novembro', value: 11 },
  { label: 'Dezembro', value: 12 },
];

const statusLabels = {
  aprovado_em: 'Aprovados',
  entregue_em: 'Entregues',
  pago_em: 'Pagos',
};

const emptyBlock = {
  ano: 0,
  mes: 0,
  campo_data: 'criado_em',
  faturamento_mensal: 0,
  quantidade_orcamentos: 0,
  ticket_medio: 0,
  quantidade_itens_vendidos: 0,
  quantidade_itens_unicos_vendidos: 0,
  margem_lucro_total: 0,
  participacao_tabela_preco: [],
  top_produtos_por_faturamento: [],
  top_produtos_por_unidades: [],
};

function formatMoneyFromInt(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format((Number(value) || 0) / 100);
}

function formatInteger(value) {
  return new Intl.NumberFormat('pt-BR').format(Number(value) || 0);
}

function formatPercent(value) {
  return `${((Number(value) || 0) * 100).toFixed(2)}%`;
}

function getErrorMessage(error) {
  return (
    error?.message ||
    error?.response?.data?.error ||
    'Não foi possível carregar o relatório.'
  );
}

function normalizeBlock(block) {
  return {
    ...emptyBlock,
    ...(block || {}),
    participacao_tabela_preco: Array.isArray(block?.participacao_tabela_preco)
      ? block.participacao_tabela_preco
      : [],
    top_produtos_por_faturamento: Array.isArray(
      block?.top_produtos_por_faturamento
    )
      ? block.top_produtos_por_faturamento
      : [],
    top_produtos_por_unidades: Array.isArray(block?.top_produtos_por_unidades)
      ? block.top_produtos_por_unidades
      : [],
  };
}

function normalizeReport(report) {
  if (!report) return null;

  return {
    ...report,
    todos_pedidos: normalizeBlock(report.todos_pedidos),
    aprovados: normalizeBlock(report.aprovados),
    por_status: Object.entries(report.por_status || {}).reduce(
      (acc, [status, block]) => ({
        ...acc,
        [status]: normalizeBlock(block),
      }),
      {}
    ),
  };
}

function getPriceTableShare(block) {
  const byTable = (block.participacao_tabela_preco || []).reduce(
    (acc, item) => ({ ...acc, [item.tabela]: item }),
    {}
  );

  return ['A', 'B', 'C'].map((tabela) => ({
    tabela,
    faturamento: byTable[tabela]?.faturamento || 0,
    unidades: byTable[tabela]?.unidades || 0,
    participacao_receita: byTable[tabela]?.participacao_receita || 0,
  }));
}

function isEmptyReport(report) {
  if (!report) return false;

  const blocks = [
    report.todos_pedidos,
    report.aprovados,
    ...Object.values(report.por_status || {}),
  ];

  return blocks.every(
    (block) =>
      !block.faturamento_mensal &&
      !block.quantidade_orcamentos &&
      !block.quantidade_itens_vendidos
  );
}

function SummaryCard({ title, value, subtitle, icon, loading }) {
  return (
    <div className="bg-white border border-[#ebebeb] rounded-lg p-4 min-h-[112px] flex justify-between gap-3">
      <div className="flex flex-col gap-2 min-w-0">
        <span className="text-sm text-gray-500">{title}</span>
        {loading ? (
          <Skeleton.Input active size="small" className="!w-[140px]" />
        ) : (
          <strong className="text-[22px] leading-tight text-blue-dark-default break-words">
            {value}
          </strong>
        )}
        {subtitle && <span className="text-xs text-gray-500">{subtitle}</span>}
      </div>
      <div className="w-11 h-11 rounded-lg bg-[#eef3f8] text-blue-default flex items-center justify-center shrink-0">
        {icon}
      </div>
    </div>
  );
}

function ComparisonCard({ title, block, colorClass, loading }) {
  return (
    <div className="border border-[#ebebeb] rounded-lg p-4 bg-white">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-blue-dark-default">
            {title}
          </h3>
          <span className="text-xs text-gray-500">
            Campo técnico: {block.campo_data}
          </span>
        </div>
        <span className={`w-3 h-3 rounded-full ${colorClass}`} />
      </div>

      {loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Metric label="Faturamento" value={formatMoneyFromInt(block.faturamento_mensal)} />
          <Metric label="Orçamentos" value={formatInteger(block.quantidade_orcamentos)} />
          <Metric label="Ticket médio" value={formatMoneyFromInt(block.ticket_medio)} />
          <Metric label="Margem" value={formatMoneyFromInt(block.margem_lucro_total)} />
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-lg bg-gray-50 p-3">
      <span className="block text-xs text-gray-500">{label}</span>
      <strong className="text-sm text-gray-900">{value}</strong>
    </div>
  );
}

function PriceTableChart({ block, loading }) {
  const share = getPriceTableShare(block);
  const maxUnits = Math.max(...share.map((item) => item.unidades), 1);

  return (
    <Card title="Participação por tabela de preço" icon={<AiOutlineBarChart />}>
      <div className="p-4">
        {loading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : (
          <div className="flex flex-col gap-4">
            {share.map((item) => (
              <div key={item.tabela} className="flex flex-col gap-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-blue-default text-white flex items-center justify-center font-semibold">
                      {item.tabela}
                    </span>
                    <span className="text-gray-700">
                      {formatMoneyFromInt(item.faturamento)}
                    </span>
                  </div>
                  <span className="text-gray-500">
                    {formatPercent(item.participacao_receita)}
                  </span>
                </div>
                <div className="h-3 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full bg-green-default"
                    style={{
                      width: `${Math.max(
                        item.participacao_receita * 100,
                        item.unidades > 0 ? 4 : 0
                      )}%`,
                    }}
                  />
                </div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full bg-blue-default"
                    style={{ width: `${(item.unidades / maxUnits) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500">
                  {formatInteger(item.unidades)} unidades
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function StatusSummary({ report, loading }) {
  const statusList = Object.entries(report?.por_status || {}).map(
    ([status, block]) => ({
      status,
      label: statusLabels[status] || status,
      ...block,
    })
  );

  return (
    <Card title="Status operacionais" icon={<LuCalendarRange />}>
      <div className="p-4">
        {loading ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : statusList.length === 0 ? (
          <Empty description="Nenhum status selecionado" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {statusList.map((item) => (
              <div
                key={item.status}
                className="border border-[#ebebeb] rounded-lg p-4 bg-gray-50"
              >
                <h3 className="font-semibold text-blue-dark-default">
                  {item.label}
                </h3>
                <span className="text-xs text-gray-500">{item.campo_data}</span>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Metric
                    label="Faturamento"
                    value={formatMoneyFromInt(item.faturamento_mensal)}
                  />
                  <Metric
                    label="Orçamentos"
                    value={formatInteger(item.quantidade_orcamentos)}
                  />
                  <Metric
                    label="Margem"
                    value={formatMoneyFromInt(item.margem_lucro_total)}
                  />
                  <Metric
                    label="Itens"
                    value={formatInteger(item.quantidade_itens_vendidos)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function ProductsRanking({ title, products, loading }) {
  const columns = [
    {
      title: 'Produto',
      dataIndex: 'nome',
      key: 'nome',
      render: (value, record) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{value}</span>
          <span className="text-xs text-gray-500">#{record.fid_produto}</span>
        </div>
      ),
    },
    { title: 'Tipo', dataIndex: 'tipo', key: 'tipo' },
    {
      title: 'Unidades',
      dataIndex: 'unidades_vendidas',
      key: 'unidades_vendidas',
      render: formatInteger,
    },
    {
      title: 'Faturamento',
      dataIndex: 'faturamento',
      key: 'faturamento',
      render: formatMoneyFromInt,
    },
    {
      title: 'Margem',
      dataIndex: 'margem_lucro',
      key: 'margem_lucro',
      render: formatMoneyFromInt,
    },
    {
      title: 'Margem %',
      dataIndex: 'margem_percentual',
      key: 'margem_percentual',
      render: (value) => `${(Number(value) || 0).toFixed(2)}%`,
    },
  ];

  return (
    <Card title={title} icon={<MdOutlineInventory2 />}>
      <div className="p-4">
        <Table
          rowKey={(record) => `${title}-${record.fid_produto}`}
          columns={columns}
          dataSource={products}
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'Nenhum produto no período' }}
          scroll={{ x: true }}
        />
      </div>
    </Card>
  );
}

const RelatoriosPage = () => {
  const today = useMemo(() => new Date(), []);
  const [filters, setFilters] = useState({
    ano: today.getFullYear(),
    mes: today.getMonth() + 1,
    statusFields: ['aprovado_em', 'entregue_em', 'pago_em'],
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        ano: String(filters.ano),
        mes: String(filters.mes),
      });

      if (filters.statusFields.length > 0) {
        params.set('status_fields', filters.statusFields.join(','));
      }

      const response = await CustomService.getRequest(
        `/interno/relatorios/mensal?${params.toString()}`
      );
      setReport(normalizeReport(response));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const mainBlock = report?.todos_pedidos || emptyBlock;
  const approvedBlock = report?.aprovados || emptyBlock;
  const hasNoData = !loading && !error && isEmptyReport(report);

  return (
    <div className="flex flex-col w-full gap-4">
      <Card title="Relatórios mensais" icon={<AiOutlineBarChart />}>
        <div className="p-4 flex flex-col gap-4">
          <div className="grid grid-cols-1 lg:grid-cols-[160px_180px_minmax(260px,1fr)_auto] gap-3 items-end">
            <label className="flex flex-col gap-1 text-sm text-gray-600">
              Mês
              <Select
                value={filters.mes}
                options={monthOptions}
                onChange={(mes) => setFilters((prev) => ({ ...prev, mes }))}
              />
            </label>

            <label className="flex flex-col gap-1 text-sm text-gray-600">
              Ano
              <InputNumber
                min={2000}
                max={3000}
                value={filters.ano}
                className="!w-full"
                onChange={(ano) =>
                  setFilters((prev) => ({
                    ...prev,
                    ano: ano || today.getFullYear(),
                  }))
                }
              />
            </label>

            <label className="flex flex-col gap-1 text-sm text-gray-600">
              Status comparativos
              <Select
                mode="multiple"
                value={filters.statusFields}
                options={statusOptions}
                onChange={(statusFields) =>
                  setFilters((prev) => ({ ...prev, statusFields }))
                }
              />
            </label>

            <Button
              type="primary"
              icon={<AiOutlineReload />}
              loading={loading}
              onClick={fetchReport}
              style={{ backgroundColor: '#054F72' }}
            >
              Atualizar
            </Button>
          </div>

          {error && (
            <Alert
              type="error"
              showIcon
              message="Erro ao carregar relatório"
              description={error}
              action={
                <Button size="small" danger onClick={fetchReport}>
                  Tentar novamente
                </Button>
              }
            />
          )}

          {hasNoData && (
            <Alert
              type="info"
              showIcon
              message="Sem dados para o período"
              description="Os filtros continuam disponíveis para consultar outro mês."
            />
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        <SummaryCard
          title="Faturamento mensal"
          value={formatMoneyFromInt(mainBlock.faturamento_mensal)}
          subtitle="Todos os pedidos por criação"
          icon={<MdAttachMoney size={26} />}
          loading={loading}
        />
        <SummaryCard
          title="Orçamentos"
          value={formatInteger(mainBlock.quantidade_orcamentos)}
          subtitle="Quantidade no período"
          icon={<AiOutlineShoppingCart size={26} />}
          loading={loading}
        />
        <SummaryCard
          title="Ticket médio"
          value={formatMoneyFromInt(mainBlock.ticket_medio)}
          subtitle="Faturamento por pedido"
          icon={<LuBadgeDollarSign size={26} />}
          loading={loading}
        />
        <SummaryCard
          title="Itens vendidos"
          value={formatInteger(mainBlock.quantidade_itens_vendidos)}
          subtitle={`${formatInteger(
            mainBlock.quantidade_itens_unicos_vendidos
          )} produtos únicos`}
          icon={<MdOutlineInventory2 size={24} />}
          loading={loading}
        />
        <SummaryCard
          title="Margem total"
          value={formatMoneyFromInt(mainBlock.margem_lucro_total)}
          subtitle="Margem estimada"
          icon={<AiOutlineBarChart size={26} />}
          loading={loading}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ComparisonCard
          title="Todos os pedidos"
          block={mainBlock}
          colorClass="bg-blue-default"
          loading={loading}
        />
        <ComparisonCard
          title="Aprovados"
          block={approvedBlock}
          colorClass="bg-green-default"
          loading={loading}
        />
      </div>

      <StatusSummary report={report} loading={loading} />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <PriceTableChart block={mainBlock} loading={loading} />
        <ProductsRanking
          title="Top produtos por faturamento"
          products={mainBlock.top_produtos_por_faturamento}
          loading={loading}
        />
      </div>

      <ProductsRanking
        title="Top produtos por unidades"
        products={mainBlock.top_produtos_por_unidades}
        loading={loading}
      />
    </div>
  );
};

export default RelatoriosPage;
